<template>
  <div class="col-12">
    <div class="card mb-5 me-4" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px;">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
              {{ $t('classSubjects.addTitle', { className }) }}
            </h6>
          </div>
        </div>
      </div>
      
      <div class="card-body px-0 pb-2 pe-3">
        <div class="p-4">
          <div class="card card-plain">
            <div class="card-body">
              <form @submit.prevent="handleSubmit">
                <div class="mb-3">
                  <label class="form-label">{{ $t('classSubjects.selectSubject') }}</label>
                  <select 
                    class="form-control borderless-input"
                    v-model="selectedSubjectId"
                    :disabled="isLoading || availableSubjects.length === 0"
                  >
                    <option value="" disabled>{{ $t('classSubjects.chooseSubject') }}</option>
                    <option 
                      v-for="subject in availableSubjects" 
                      :key="subject.id" 
                      :value="subject.id"
                    >
                      {{ subject.name }} ({{ subject.code }})
                    </option>
                    <option v-if="availableSubjects.length === 0" disabled>
                      {{ $t('classSubjects.noAvailableSubjects') }}
                    </option>
                  </select>
                </div>
  
                <div class="mb-3">
                  <label class="form-label">{{ $t('classSubjects.statusLabel') }}</label>
                  <select 
                    class="form-select borderless-input"
                    v-model="status"
                    :disabled="isLoading"
                  >
                    <option value="active">{{ $t('classSubjects.active') }}</option>
                    <option value="inactive">{{ $t('classSubjects.inactive') }}</option>
                  </select>
                </div>
  
                <div class="d-flex gap-3 mt-4">
                  <button 
                    type="submit" 
                    class="btn btn-primary btn-sm flex-grow-1"
                    :disabled="isLoading || !selectedSubjectId"
                  >
                    <span v-if="isLoading">
                      <i class="fas fa-spinner fa-spin me-2"></i>
                      {{ $t('classSubjects.adding') }}
                    </span>
                    <span v-else>
                      <i class="fas fa-plus ms-2"></i>
                      {{ $t('classSubjects.addButton') }}
                    </span>
                  </button>
                  <button 
                    type="button" 
                    class="btn btn-secondary btn-sm flex-grow-1"
                    @click="handleCancel"
                    :disabled="isLoading"
                  >
                    <i class="fas fa-times ms-2"></i>
                    {{ $t('classSubjects.cancel') }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
const { t } = useI18n();

const toast = useToast();
const router = useRouter();
const route = useRoute();
const store = useStore();

const classId = route.params.id;
const selectedSubjectId = ref(null);
const status = ref('active');
const isLoading = ref(false);

const className = computed(() => store.state.classes.currentClass?.name || '');

const availableSubjects = computed(() => {
  const allSubjects = store.state.subjects?.subjects || [];
  const classSubjects = store.state.classsubjects?.classSubjects || [];
  return allSubjects.filter(subject => !classSubjects.some(cs => cs.id === subject.id));
});

onMounted(async () => {
  isLoading.value = true;
  try {
    await store.dispatch('subjects/fetchSubjects'); 
    if (availableSubjects.value.length === 0) {
      toast.warning(t('classSubjects.noAvailableSubjects'));
    }
  } catch (error) {
    toast.error(t('classSubjects.fetchError'));
  } finally {
    isLoading.value = false;
  }
});
  
const handleSubmit = async () => {
  if (!selectedSubjectId.value) return;
  
  isLoading.value = true;
  
  try {
    await store.dispatch('classsubjects/addSubjectToClass', {
      classId,
      subject_id: selectedSubjectId.value,
      status: status.value
    });
    
    toast.success(t('classSubjects.addSuccess'));
    router.push(`/classes/${classId}/subjects`);
  } catch (error) {
    console.error('Error adding subject:', error);
    if (error.response?.data?.errors) {
      const errors = Object.values(error.response.data.errors).flat();
      errors.forEach(err => toast.error(err));
    } else {
      toast.error(error.response?.data?.message || t('classSubjects.addError'));
    }
  } finally {
    isLoading.value = false;
  }
};
  
const handleCancel = () => {
  router.push(`/classes/${classId}/subjects`);
};
</script>
